// Shared room bootstrap: wire a grant into the store and connect the engine.
// Used by BOTH surfaces that enter a room — the meeting page (§5.4 join flow)
// and the call page (/call/:id) — so policy flags, E2EE model selection, and
// TURN handling can never drift between the two.
//
// Throws on connect failure; the caller owns its own phase/error UI.

import type { JoinGrant } from '../api';
import { readModelBSecret } from '../api';
import { engine } from '../engine';
import {
  resetCallState,
  setFileSharingAllowed,
  setKnockSound,
  setMeetingLimit,
  setMyRole,
  setNotesAllowed,
  setPollsAllowed,
  setRecordingAllowed,
  setRoomToken,
  setTimerAllowed,
} from '../store/callStore';

export interface ConnectChoices {
  micEnabled: boolean;
  camEnabled: boolean;
  /** Model B passphrase typed in the lobby (§5.2), when the link lacks #k=. */
  passphrase?: string;
}

export async function connectRoom(grant: JoinGrant, choices: ConnectChoices): Promise<void> {
  resetCallState();
  setMyRole(grant.role as 'host' | 'cohost' | 'attendee' | 'viewonly');
  setRoomToken(grant.token); // authorizes file presign (F-COM-5)
  setRecordingAllowed(grant.allowRecording !== false); // Phase C org policy
  setFileSharingAllowed(grant.allowFileSharing !== false); // F-COM-5 org policy
  setPollsAllowed(grant.allowPolls !== false); // Meeting Tools org policy
  setTimerAllowed(grant.allowTimer !== false);
  setNotesAllowed(grant.allowNotes !== false);
  setMeetingLimit(grant.endsAt ?? 0, grant.durationCapMin ?? 0); // Phase C duration cap
  setKnockSound(grant.knockSound ?? 'ding'); // org-wide join-request chime

  // Model B (zero-knowledge): the client's own passphrase, taken from the link
  // fragment (#k=…, never sent to a server) or the lobby prompt.
  let e2ee: { model: 'a' | 'b'; secret: string; sessionId: string } | undefined;
  if (grant.e2ee?.model === 'a' && grant.e2ee.secret) {
    e2ee = { model: 'a', secret: grant.e2ee.secret, sessionId: grant.sessionId };
  } else if (grant.e2ee?.model === 'b') {
    const passphrase = readModelBSecret() ?? choices.passphrase ?? '';
    e2ee = { model: 'b', secret: passphrase, sessionId: grant.sessionId };
  }

  // TURN fallback relay: offer the relay creds; ?forcerelay=1 pins relay-only
  // for the acceptance test (proves coturn is actually used), never normal use.
  const forceRelay = new URLSearchParams(window.location.search).get('forcerelay') === '1';
  await engine.connect(
    grant.wsUrl,
    grant.token,
    e2ee,
    { iceServers: grant.iceServers, forceRelay },
    grant.audio,
  );
  if (choices.micEnabled) await engine.setMicEnabled(true);
  if (choices.camEnabled) await engine.setCameraEnabled(true);
}

